import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { OrderJobTechnicianFeModel } from 'pos-shared/models/orderSchema.ts';

type JobTechniciansProviderProps = {
  initialTechnicians: OrderJobTechnicianFeModel[];
  children: ReactNode;
};

interface JobTechniciansState {
  newTechnicians: OrderJobTechnicianFeModel[];
  updatedTechnicians: OrderJobTechnicianFeModel[];
  deletedTechnicians: OrderJobTechnicianFeModel[];
  technicians: OrderJobTechnicianFeModel[];
}

interface JobTechniciansContextProps extends JobTechniciansState {
  addTechnician: (technician: OrderJobTechnicianFeModel) => void;
  updateTechnician: (
    technicianId: string,
    technician: Partial<OrderJobTechnicianFeModel>,
    existingTechnicianId?: string
  ) => void;
  removeTechnician: (technicianId: string) => void;
  resetTechnicians: (newTechnicians: OrderJobTechnicianFeModel[]) => void;
}

const initialTechniciansState: JobTechniciansState = {
  newTechnicians: [],
  updatedTechnicians: [],
  deletedTechnicians: [],
  technicians: [],
};

const initialContextState: JobTechniciansContextProps = {
  ...initialTechniciansState,
  addTechnician: () => undefined,
  updateTechnician: () => undefined,
  removeTechnician: () => undefined,
  resetTechnicians: () => undefined,
};

type TechnicianActions =
  | {
      type: 'add';
      technician: OrderJobTechnicianFeModel;
      technicianId?: never;
      existingTechnicianId?: never;
    }
  | {
      type: 'update';
      technician: Partial<OrderJobTechnicianFeModel>;
      technicianId: string;
      existingTechnicianId?: string;
    }
  | {
      type: 'remove';
      technician?: never;
      technicianId: string;
      existingTechnicianId?: never;
    }
  | {
      type: 'reset';
      technician: OrderJobTechnicianFeModel[];
      technicianId?: never;
      existingTechnicianId?: never;
    };

const JobTechniciansContext =
  createContext<JobTechniciansContextProps>(initialContextState);

const technicianReducer = (
  state: OrderJobTechnicianFeModel[],
  action: TechnicianActions
): OrderJobTechnicianFeModel[] => {
  switch (action.type) {
    case 'add':
      return [...state, action.technician];
    case 'update':
      return state.map((tech) =>
        tech.userId === action.technicianId
          ? {
              ...tech,
              ...action.technician,
              ...(action.technician.effortPercentage && {
                effortPercentage:
                  action.technician.effortPercentage >
                  100 -
                    state.reduce(
                      (total, curTech) =>
                        total + (curTech.effortPercentage ?? 0),
                      0
                    ) +
                    (tech.effortPercentage ?? 0)
                    ? 100 -
                      state.reduce(
                        (total, curTech) =>
                          total + (curTech.effortPercentage ?? 0),
                        0
                      )
                    : action.technician.effortPercentage,
              }),
            }
          : tech
      );
    case 'remove':
      return state.filter((tech) => tech.userId !== action.technicianId);
    case 'reset':
      return action.technician;
    default:
      return state;
  }
};

const isDeepEqual = (
  obj1: OrderJobTechnicianFeModel,
  obj2: OrderJobTechnicianFeModel
): boolean => {
  if (obj1 === obj2) {
    return true;
  }

  if (!obj1 || !obj2) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every((key) => {
    const val1 = obj1[key as keyof OrderJobTechnicianFeModel];
    const val2 = obj2[key as keyof OrderJobTechnicianFeModel];
    return val1 === val2;
  });
};

export const JobTechniciansProvider = ({
  children,
  initialTechnicians,
}: JobTechniciansProviderProps) => {
  const [state, dispatch] = useReducer(technicianReducer, initialTechnicians);

  const addTechnician = (technician: OrderJobTechnicianFeModel) =>
    dispatch({ type: 'add', technician });
  const updateTechnician = (
    technicianId: string,
    technician: Partial<OrderJobTechnicianFeModel>,
    existingTechnicianId?: string
  ) =>
    dispatch({
      type: 'update',
      technicianId,
      technician,
      existingTechnicianId,
    });
  const removeTechnician = (technicianId: string) =>
    dispatch({ type: 'remove', technicianId });
  const resetTechnicians = (newTechnicians: OrderJobTechnicianFeModel[]) =>
    dispatch({ type: 'reset', technician: newTechnicians });

  const newTechnicians = useMemo(
    () =>
      state.filter(
        (tech) =>
          !initialTechnicians.some((initial) => initial.userId === tech.userId)
      ),
    [state, initialTechnicians]
  );

  const updatedTechnicians = useMemo(
    () =>
      state.filter((tech) => {
        const initialTech = initialTechnicians.find(
          (t) => t.userId === tech.userId
        );
        return initialTech && !isDeepEqual(tech, initialTech);
      }),
    [state, initialTechnicians]
  );

  const deletedTechnicians = useMemo(
    () =>
      initialTechnicians.filter(
        (initial) => !state.some((tech) => tech.userId === initial.userId)
      ),
    [state, initialTechnicians]
  );

  return (
    <JobTechniciansContext.Provider
      value={{
        technicians: state,
        newTechnicians,
        updatedTechnicians,
        deletedTechnicians,
        addTechnician,
        updateTechnician,
        removeTechnician,
        resetTechnicians,
      }}
    >
      {children}
    </JobTechniciansContext.Provider>
  );
};

export const useJobTechnicians = () => useContext(JobTechniciansContext);
